import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Play } from 'lucide-react';

export default function HomePage() {
  const videoRef = useRef(null);

  const scrollToVideo = () => {
    videoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => {
      const video = videoRef.current?.querySelector('video');
      if (video) video.play();
    }, 600);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      {/* ── Navbar ── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100/60">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2.5">
              <img src="/assets/britehub-logo.png" alt="SLL BriteHub" className="h-9 w-9" />
              <span className="text-xl font-bold tracking-tight text-[#121E30]">SLL BriteHub</span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/members" className="text-sm font-medium text-[#121E30]/70 hover:text-[#3E60B1] transition-colors">Community</Link>
              <Link to="/forum" className="text-sm font-medium text-[#121E30]/70 hover:text-[#3E60B1] transition-colors">Forum</Link>
              <Link to="/news" className="text-sm font-medium text-[#121E30]/70 hover:text-[#3E60B1] transition-colors">Discovery</Link>
              <Link to="/britelabs" className="text-sm font-medium text-[#121E30]/70 hover:text-[#3E60B1] transition-colors">BriteLab</Link>
              <Link to="/login" data-testid="nav-signup-btn"
                className="px-5 py-2 rounded-md bg-[#4a9b5e] text-white text-sm font-semibold hover:bg-[#3d8a50] transition-colors">
                Sign Up
              </Link>
              <Link to="/login" data-testid="nav-signin-btn"
                className="px-5 py-2 rounded-md border border-slate-300 text-[#121E30]/70 text-sm font-semibold hover:border-[#3E60B1] hover:text-[#3E60B1] transition-colors">
                Sign In
              </Link>
            </div>
            <div className="flex md:hidden gap-2">
              <Link to="/login" className="px-4 py-2 rounded-md bg-[#4a9b5e] text-white text-sm font-semibold">Sign Up</Link>
              <Link to="/login" className="px-4 py-2 rounded-md border border-slate-300 text-[#121E30]/70 text-sm font-semibold">Sign In</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="relative pt-16 overflow-hidden" style={{ minHeight: '80vh' }}>
        {/* Background image — full coverage */}
        <div className="absolute inset-0">
          <img src="/assets/hero-bg.png" alt=""
            className="w-full h-full object-cover"
            style={{ opacity: 0.5 }} />
          {/* White fade from left — strong on the left, transparent on the right */}
          <div className="absolute inset-0"
            style={{ background: 'linear-gradient(to right, white 0%, white 25%, rgba(255,255,255,0.92) 35%, rgba(255,255,255,0.6) 50%, rgba(255,255,255,0.15) 70%, rgba(255,255,255,0) 100%)' }} />
          {/* Top white fade for navbar blend */}
          <div className="absolute inset-0"
            style={{ background: 'linear-gradient(to bottom, white 0%, rgba(255,255,255,0) 18%)' }} />
        </div>

        {/* Content */}
        <div className="relative container mx-auto px-6 flex items-center" style={{ minHeight: 'calc(80vh - 64px)' }}>
          <div className="grid lg:grid-cols-2 gap-8 items-center w-full">
            {/* Left text — over white area */}
            <div className="max-w-xl z-10">
              <h1 className="text-4xl sm:text-5xl lg:text-[3.4rem] font-bold leading-[1.12] tracking-tight text-[#121E30] mb-5" data-testid="hero-heading">
                Connecting the global sustainable logistics community
              </h1>
              <p className="text-base sm:text-lg text-slate-500 leading-relaxed mb-8 max-w-md">
                Join SLL BriteHub to collaborate, share knowledge and drive impactful action for a sustainable future
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/login" data-testid="get-started-button">
                  <button className="px-7 py-3 rounded-md bg-[#4a9b5e] text-white font-semibold text-base hover:bg-[#3d8a50] transition-colors shadow-lg shadow-green-700/15">
                    Get Started
                  </button>
                </Link>
                {/* Watch Intro button hidden while the intro video section is hidden */}
                {false && (
                  <button data-testid="watch-intro-button" onClick={scrollToVideo}
                    className="px-7 py-3 rounded-md border-2 border-slate-300 text-[#121E30] font-semibold text-base hover:border-[#3E60B1] hover:text-[#3E60B1] transition-colors flex items-center gap-2.5">
                    <Play className="h-4 w-4 fill-current" /> Watch Intro
                  </button>
                )}
              </div>
            </div>

            {/* Skewed logo on right — over the globe area */}
            <div className="hidden lg:flex justify-center items-center z-10">
              <img src="/assets/logo-skew.png" alt="SLL BriteHub"
                className="w-80 h-80 xl:w-[420px] xl:h-[420px] drop-shadow-[0_0_40px_rgba(155,194,105,0.2)]" />
            </div>
          </div>
        </div>
      </section>

      {/* ── Video Section ── (hidden for now per stakeholder request) */}
      {false && (
      <section ref={videoRef} className="relative py-16 px-6 bg-[#f8fafb]" data-testid="video-section">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-[#121E30] mb-3 tracking-tight">
            See BriteHub in Action
          </h2>
          <p className="text-center text-slate-500 mb-8 max-w-lg mx-auto">
            Watch how our community is driving sustainable change in logistics
          </p>
          <div className="rounded-2xl overflow-hidden shadow-2xl shadow-slate-300/50 border border-slate-200/60 bg-black">
            <video
              data-testid="intro-video"
              controls
              preload="metadata"
              playsInline
              poster=""
              className="w-full aspect-video"
              src="https://customer-assets.emergentagent.com/job_5844fe90-bb9b-446a-8ab4-fd72e31d1ed9/artifacts/6swmpdwi_Britehub.mp4"
            />
          </div>
        </div>
      </section>
      )}

      {/* ── Features Section — bg image continues behind ── */}
      <section className="relative pt-10 pb-16 px-6 overflow-hidden">
        {/* Continuing subtle bg */}
        <div className="absolute inset-0">
          <img src="/assets/hero-bg.png" alt="" className="w-full h-full object-cover" style={{ opacity: 0.09 }} />
          <div className="absolute inset-0 bg-white/70" />
        </div>

        <div className="relative container mx-auto max-w-5xl z-10">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center text-[#121E30] mb-12 tracking-tight" data-testid="features-heading">
            Build a brighter, more sustainable future
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            <div data-testid="feature-engage" className="bg-white/90 backdrop-blur-sm rounded-2xl p-7 shadow-lg shadow-slate-200/50 border border-slate-100/80 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <div className="mb-4 flex items-center gap-4">
                <img src="/assets/pin-icon.png" alt="" className="w-12 h-12" />
                <h3 className="text-lg font-bold text-[#121E30]">Engage with Peers</h3>
              </div>
              <p className="text-sm text-slate-500 leading-relaxed">
                Engage in discussions with sustainability leads & innovators
              </p>
            </div>

            <div data-testid="feature-learn" className="bg-white/90 backdrop-blur-sm rounded-2xl p-7 shadow-lg shadow-slate-200/50 border border-slate-100/80 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <div className="mb-4 flex items-center gap-4">
                <img src="/assets/book-icon.png" alt="" className="w-12 h-12" />
                <h3 className="text-lg font-bold text-[#121E30]">Learn & Share</h3>
              </div>
              <p className="text-sm text-slate-500 leading-relaxed">
                Access and contribute to a rich library of resources, articles, and case studies
              </p>
            </div>

            <div data-testid="feature-change" className="bg-white/90 backdrop-blur-sm rounded-2xl p-7 shadow-lg shadow-slate-200/50 border border-slate-100/80 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <div className="mb-4 flex items-center gap-4">
                <img src="/assets/flask-icon.png" alt="" className="w-12 h-12" />
                <h3 className="text-lg font-bold text-[#121E30]">Support Change</h3>
              </div>
              <p className="text-sm text-slate-500 leading-relaxed">
                Create BriteLabs and progress towards solutions with varied stakeholders
              </p>
            </div>
          </div>

          {/* Community Avatars */}
          <div className="flex justify-center mt-14 mb-4">
            <div className="flex -space-x-3">
              {['KH', 'DP', 'JS', 'AL', 'MR'].map((initials, i) => (
                <div key={i}
                  className="w-11 h-11 rounded-full border-[3px] border-white shadow-md flex items-center justify-center text-xs font-bold text-white"
                  style={{ background: ['#3E60B1','#4a9b5e','#6C9BEB','#9BC269','#121E30'][i] }}>
                  {initials}
                </div>
              ))}
              <div className="w-11 h-11 rounded-full border-[3px] border-white shadow-md bg-[#4a9b5e] flex items-center justify-center text-xs font-bold text-white">
                500+
              </div>
            </div>
          </div>
          <p className="text-center text-sm text-slate-400">Join our growing community of sustainability leaders</p>
        </div>
      </section>

      {/* ── CTA Section ── */}
      <section className="relative py-20 px-6 bg-[#0a0f18] overflow-hidden">
        <img src="/assets/wave-green.png" alt=""
          className="absolute inset-0 w-full h-full object-cover pointer-events-none mix-blend-screen opacity-30" />
        <div className="relative container mx-auto text-center max-w-2xl z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-5 tracking-tight">
            Ready to begin your journey?
          </h2>
          <p className="text-lg text-white/75 mb-8 leading-relaxed">
            Join hundreds of sustainability and logistics professionals shaping a better tomorrow.
          </p>
          <Link to="/login" data-testid="cta-join-button">
            <button className="px-8 py-3.5 rounded-md bg-white text-[#121E30] font-bold text-base hover:bg-slate-100 transition-colors shadow-xl">
              Join the Community
            </button>
          </Link>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="py-10 px-6 border-t border-slate-200 bg-slate-50">
        <div className="container mx-auto text-center text-slate-500 text-sm">
          <p>&copy; 2026 Shape Tomorrow Limited. Company registration 16752504.</p>
        </div>
      </footer>
    </div>
  );
}
